import { Injectable } from '@angular/core';
import { User, UserManager, WebStorageStateStore, UserManagerSettings, OidcClientSettings } from 'oidc-client';
import { BehaviorSubject, concat, from, Observable } from 'rxjs';
import { filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { ApplicationPaths, ApplicationName } from '../../authorization/api-authorization.constants';
import { AuthService } from './auth.service';
import { AccountService } from '../Common/account.service';
import { environment } from '@env/environment';

export type IAuthenticationResult =
  SuccessAuthenticationResult |
  FailureAuthenticationResult |
  RedirectAuthenticationResult;

export interface SuccessAuthenticationResult {
  status: AuthenticationResultStatus.Success;
  state: any;
}

export interface FailureAuthenticationResult {
  status: AuthenticationResultStatus.Fail;
  message: string;
}

export interface RedirectAuthenticationResult {
  status: AuthenticationResultStatus.Redirect;
}

export enum AuthenticationResultStatus {
  Success,
  Redirect,
  Fail
}

export interface IUser {
  name?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizeService {

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  constructor(private authService: AuthService, private accountService: AccountService) {

  }
  private popUpDisabled = true;
  private userManager: UserManager;//  new UserManager(getClientSettings());
  private userSubject: BehaviorSubject<IUser | null> = new BehaviorSubject(null);

  public isAuthenticated(): Observable<boolean> {
    return this.getUser().pipe(map(u => !!u));
  }

  public getUser(): Observable<IUser | null> {
    return concat(
      this.userSubject.pipe(take(1), filter(u => !!u)),
      this.getUserFromStorage().pipe(filter(u => !!u), tap(u => this.userSubject.next(u))),
      this.userSubject.asObservable());
  }

  public getAccessToken(): Observable<string> {
    return from(this.ensureUserManagerInitialized())
      .pipe(mergeMap(() => from(this.userManager.getUser())),
        map(user => user && user.access_token));
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  public async signIn(state: any): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized();
    let user: User = null;
    try {
      user = await this.userManager.signinSilent(this.createArguments());
      this.userSubject.next(user.profile);
      if (user != undefined)
        this.authService.processLoginResponse(user, false);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log('Silent authentication error: ', silentError);

      try {
        if (this.popUpDisabled) {
          throw new Error('Popup disabled. Change \'authorize.service.ts:AuthorizeService.popupDisabled\' to false to enable it.');
        }
        user = await this.userManager.signinPopup(this.createArguments());
        this.userSubject.next(user.profile);
        if (user != undefined)
          this.authService.processLoginResponse(user, false);
        return this.success(state);
      } catch (popupError) {
        if (popupError.message === 'Popup window closed') {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error('The user closed the window.');
        } else if (!this.popUpDisabled) {
          console.log('Popup authentication error: ', popupError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager.signinRedirect(this.createArguments(state));
          //this.userManager.signinRedirect();
          return this.redirect();

        } catch (redirectError) {
          console.log('Redirect authentication error: ', redirectError);
          return this.error(redirectError);
        }
      }
    }
  }

  public async completeSignIn(url: string): Promise<IAuthenticationResult> {
    try {
      await this.ensureUserManagerInitialized();
      const user = await this.userManager.signinCallback(url);
      this.userSubject.next(user && user.profile);
      if (user != undefined) {
        this.authService.processLoginResponse(user, false);
      }
      setInterval(() => {
        // console.log('timeout executing!');
        if (this.authService.isLoggedIn) {
          this.userManager.signinSilent().then(c => { this.authService.processLoginResponse(c, false); });
        }
      }, 3300000); //55 minutes

      return this.success(user && user.state);
    } catch (error) {
      // console.log('There was an error signing in: ', error);
      return this.error('There was an error signing in.');
    }
  }

  public async signOut(state: any): Promise<IAuthenticationResult> {
    const settings: any = getClientSettings();
    try {
     
        this.createArguments(state);
        await this.userManager.removeUser();
        await this.userManager.signoutRedirect({
          post_logout_redirect_uri: settings.post_logout_redirect_uri,
          extraQueryParams: {  post_logout_redirect_uri: settings.post_logout_redirect_uri } // Manually pass logoutId
        });

    } catch (redirectSignOutError) {

      return this.error(redirectSignOutError);
    }

  }

  public async completeSignOut(url: string): Promise<IAuthenticationResult> {
    await this.ensureUserManagerInitialized();
    try {
      const state = await this.userManager.signoutCallback(url);
      this.authService.logout(); //clear local storage and session
      this.userSubject.next(null);
      return this.success(state && state.length > 0);
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error);
    }
  }

  private createArguments(state?: any): any {
    return { useReplaceToNavigate: true, data: state };
  }

  private error(message: string): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  private success(state: any): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  private redirect(): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  private async ensureUserManagerInitialized(): Promise<void> {
    if (this.userManager !== undefined) {
      return;
    }

    // const response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
    // if (!response.ok) {
    //   throw new Error(`Could not load settings for '${ApplicationName}'`);
    // }

    //const settings: any = await response.json();
    const settings: any = getClientSettings();

    settings.automaticSilentRenew = true;
    settings.includeIdTokenInSilentRenew = true;
    // settings.redirect_uri= 'https://localhost:4200/authentication/login-callback';
    // settings.post_logout_redirect_uri= 'https://localhost:4200/authentication/logout-callback';

    this.userManager = new UserManager(settings);
    this.userManager.events.addUserSignedOut(async () => {
      await this.userManager.removeUser();
      this.userSubject.next(null);
    });
  }

  private getUserFromStorage(): Observable<IUser> {
    return from(this.ensureUserManagerInitialized())
      .pipe(
        mergeMap(() => this.userManager.getUser()),
        map(u => u && u.profile));
  }
  refreshLogin() {
    // this.userManager.startSilentRenew();
    this.userManager.signinSilent().then(c => { this.authService.processLoginResponse(c, false); });
  }
  browserInfo() {
    // tslint:disable-next-line: prefer-const
    let ua = window.navigator.userAgent, tem,
      M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\.?\d+(\.\d+)*)/i) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return 'IE ' + (tem[1] || '');
    }
    if (M[1] === 'Chrome') {
      tem = ua.match(/\b(OPR|Edge)\/(\d+\.\d+)/);
      if (tem != null) { return tem.slice(1).join(' ').replace('OPR', 'Opera'); }
    }
    M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
    if ((tem = ua.match(/version\/(\d+)/i)) != null) { M.splice(1, 1, tem[1]); }
    return M.join(' - ');
  }
}

// export function getClientSettings(): UserManagerSettings {
//   return {
//     authority: 'https://localhost:44300',
//     client_id: 'angular-client',//'6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ',
//     silent_redirect_uri: 'https://localhost:44300/assets/silent-callback.html',
//     redirect_uri: 'https://localhost:44300/authentication/login-callback',
//     post_logout_redirect_uri: 'https://localhost:44300/authentication/logout-callback',
//     //response_type:"id_token token", 
//     response_type: "code",
//     scope: "openid email profile roles smartapp_api Aido.Web.API Aido.Web.SmartAPI Aido.Health.API",
//     // automaticSilentRenew: false,
//     // includeIdTokenInSilentRenew: false
//   };

export function getClientSettings(): UserManagerSettings {
  return {
    authority: environment.authorityUrl,
    client_id: environment.clientId, 
    redirect_uri: `${environment.appUrl}/authentication/login-callback`,
    post_logout_redirect_uri: `${environment.appUrl}/authentication/logout-callback`,
    silent_redirect_uri: `${environment.appUrl}/assets/silent-callback.html`,
    //response_type:"id_token token",
    response_type: "code",
    scope: "openid email profile smartapp_api smarthealth_api smartlife_api",
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: true
  };
};
